import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { textEllipsis } from 'utils/string_utils'
import { isPolicyVersionEditable } from 'utils/policy_version_utils'
import { LOCALE_MAP } from 'utils/locale-formatting'
import { formatDateTimeToDateTimeBR, formatDateToDateBR, formatDateToDateBRPolish } from 'utils/date_utils'

import { ReactComponent as MarketingIcon } from '../../../assets/marketing.svg'
import { ReactComponent as NecessaryIcon } from '../../../assets/necessary.svg'
import { ReactComponent as PreferencesIcon } from '../../../assets/preferences.svg'
import { ReactComponent as StatisticsIcon } from '../../../assets/statistics.svg'

import { Select, TextField } from 'Components/ui'

import {
  ShowIcon,
  EditIcon,
  DeleteIcon,
  PublishIcon,
  PreviewIcon,
  SyncIcon,
  ArchiveIcon,
  UnarchiveIcon,
  VisibilityIcon,
  ChartIcon,
} from './icons'

import {
  TableContainer,
  SearchHeader,
  Title,
  Table,
  TableHead,
  TableHeadCell,
  SortArrow,
  TableBody,
  TableRow,
  TableCell,
  NoDataCell,
  ActionCell,
  ActionIcon,
  PaginationWrapper,
  RowsPerPage,
  PageInfo,
  PageButton,
} from './styles'

export const DefaultTable = (props) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('common')
  const {
    applyMB = false,
    cells,
    link,
    hideDelete,
    hideEdit,
    hideShow,
    id,
    onClick,
    onClickArchive,
    onClickDelete,
    onClickEdit,
    onClickShow,
    onClickStatus,
    onClickUnarchive,
    onClickDashboard,
    onClickModalDetail,
    rows,
    showArchive = false,
    showModalDetail = false,
    showNoDataLine = false,
    showDashboard = false,
    useOnClickDefault = false,
    enableSearch = false,
    pagination = false,
    title = 'Tabela',
    userId,
    expandable = false,
    renderExpanded = null,
  } = props

  const [order, setOrder] = useState('asc')
  const [orderBy, setOrderBy] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [openRows, setOpenRows] = useState(new Set())

  const toggleRow = useCallback((key) => {
    setOpenRows((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const handleChangePage = (_event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(parseInt(value, 10))
    setPage(0)
  }

  useEffect(() => {
    setPage(0)
  }, [searchTerm, orderBy, order])

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(columnId)
  }

  const filteredCells = useMemo(() => {
    if (!searchTerm) return cells || []
    return (cells || []).filter((item) =>
      rows.some((row) =>
        item[row.id]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    )
  }, [cells, searchTerm, rows])

  const sortedCells = useMemo(() => {
    if (!orderBy) return filteredCells
    const locale = LOCALE_MAP[i18n.language] || 'pt-BR'
    const collator = new Intl.Collator(locale, { sensitivity: 'base' })
    return [...filteredCells].sort((a, b) => {
      const aVal = a[orderBy]
      const bVal = b[orderBy]
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return order === 'asc' ? collator.compare(aVal, bVal) : collator.compare(bVal, aVal)
      }
      if (aVal < bVal) return order === 'asc' ? -1 : 1
      if (aVal > bVal) return order === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredCells, orderBy, order, i18n.language])

  const paginatedCells = useMemo(() => {
    if (!pagination) return sortedCells
    return sortedCells.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  }, [sortedCells, page, rowsPerPage, pagination])

  const noData = () => {
    return !cells || cells.length === 0
  }

  const renderBooleanCell = (item, row) => {
    const isInvertedField =
      row.id === 'developedInHouse' ||
      row.id === 'pii' ||
      row.id === 'dpia' ||
      row.id === 'lia' ||
      row.id === 'saas'
    const backgroundColor = isInvertedField
      ? (item[row.id] ? 'var(--vo-danger)' : 'var(--vo-success)')
      : (item[row.id] ? 'var(--vo-success)' : 'var(--vo-danger)')

    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor,
            display: 'inline-block',
          }}
        />
        {item[row.id] ? t('boolean.yes') : t('boolean.no')}
      </span>
    )
  }

  const renderActions = (item, row) => {
    const ActionWrapper = ({ title, onClick, children }) => (
      <ActionIcon title={title} onClick={onClick}>
        {children}
      </ActionIcon>
    )

    return (
      <ActionCell $minWidth={row?.minWidth}>
        {!hideShow && (
          <ActionWrapper title={t('tooltips.view')}>
            <ShowIcon
              onClick={() => {
                if (onClickShow) {
                  onClickShow(item)
                } else {
                  navigate(`/${link}/show/${item?._id?.toString() ?? id}`)
                }
              }}
            />
          </ActionWrapper>
        )}
        {!hideEdit && (
          <ActionWrapper title={t('tooltips.edit')}>
            <EditIcon
              onClick={() => {
                if (onClickEdit) {
                  onClickEdit(item)
                } else {
                  navigate(`/${link}/edit/${item?._id?.toString() ?? id}`)
                }
              }}
            />
          </ActionWrapper>
        )}
        {!hideDelete && (
          <ActionWrapper title={t('tooltips.delete')}>
            <DeleteIcon onClick={() => onClickDelete(item)} />
          </ActionWrapper>
        )}
        {(!!showArchive && !item?.archived) && (
          <ActionWrapper title={t('tooltips.archive')}>
            <ArchiveIcon onClick={() => onClickArchive(item)} />
          </ActionWrapper>
        )}
        {(!!showArchive && item?.archived) && (
          <ActionWrapper title={t('tooltips.unarchive')}>
            <UnarchiveIcon onClick={() => onClickUnarchive(item)} />
          </ActionWrapper>
        )}
        {!!showDashboard && (
          <ActionWrapper title={t('tooltips.dashboard')}>
            <ChartIcon
              onClick={() => {
                if (onClickDashboard) {
                  onClickDashboard(item)
                } else {
                  navigate(`/${link}/dashboard/${item?._id?.toString() ?? id}`)
                }
              }}
            />
          </ActionWrapper>
        )}
        {!!showModalDetail && (
          <ActionWrapper title={t('tooltips.detail')}>
            <VisibilityIcon
              onClick={() => {
                if (onClickModalDetail) {
                  onClickModalDetail(item)
                }
              }}
            />
          </ActionWrapper>
        )}
      </ActionCell>
    )
  }

  const renderActionsDatamapping = (item, row, index) => {
    const ActionWrapper = ({ title, onClick, children }) => (
      <ActionIcon title={title} onClick={onClick}>
        {children}
      </ActionIcon>
    )

    return (
      <ActionCell $minWidth={row?.minWidth}>
        {!hideShow && (
          <ActionWrapper title={t('tooltips.view')}>
            <ShowIcon
              onClick={() => {
                if (onClickShow) {
                  onClickShow(index)
                } else {
                  navigate(`/${link}/show/${item?._id?.toString() ?? id}`)
                }
              }}
            />
          </ActionWrapper>
        )}
        {!hideEdit && (
          <ActionWrapper title={t('tooltips.edit')}>
            <EditIcon
              onClick={() => {
                if (onClickEdit) {
                  onClickEdit(index)
                } else {
                  navigate(`/${link}/edit/${item?._id?.toString() ?? id}`)
                }
              }}
            />
          </ActionWrapper>
        )}
        {!hideDelete && (
          <ActionWrapper title={t('tooltips.delete')}>
            <DeleteIcon onClick={() => onClickDelete(item, index)} />
          </ActionWrapper>
        )}
      </ActionCell>
    )
  }

  const renderActionsPolicy = (item, row) => {
    const ActionWrapper = ({ title, onClick, children }) => (
      <ActionIcon title={title} onClick={onClick}>
        {children}
      </ActionIcon>
    )

    const canDelete = (policy) => {
      if (hideDelete) return false
      if (policy.policyVersions) {
        if (policy.policyVersions.find((policyVersion) => policyVersion.publishedDate != null)) {
          return false
        }
      }
      return true
    }

    const canChangeStatus = (policy) => {
      if (policy.lastPolicyVersionStatus !== 'Iniciado' && policy.lastPolicyVersionStatus !== undefined) {
        var hasPolicyVersionToChangeStatus = policy.policyVersions?.filter(policyVersion =>
          policyVersion.status !== 'Iniciado' && policyVersion.status !== 'Publicado'
        )
        if (hasPolicyVersionToChangeStatus.length > 0) {
          var policyVersion = hasPolicyVersionToChangeStatus[0]
          if (
            (policyVersion.status === 'Concluído' && policyVersion.reviewer._id.toString() === userId) ||
            (policyVersion.status === 'Revisado' && policyVersion.approver._id.toString() === userId) ||
            (policyVersion.status === 'Aprovado' && policyVersion.author._id.toString() === userId)
          ) {
            return true
          }
        }
      }
      return false
    }

    return (
      <ActionCell $minWidth={row?.minWidth}>
        {!hideShow && (
          <ActionWrapper title={t('tooltips.view')}>
            <ShowIcon onClick={() => navigate(`/${link}/show/${item?._id?.toString()}`)} />
          </ActionWrapper>
        )}
        {canChangeStatus(item) && (
          <ActionWrapper title={t('tooltips.changeStatus')}>
            <PublishIcon
              onClick={() => navigate(
                `/${link}/show/${item?._id?.toString()}/versions/${item.policyVersions.findIndex(
                  policyVersion => policyVersion.status !== 'Iniciado' && policyVersion.status !== 'Publicado'
                )}`
              )}
            />
          </ActionWrapper>
        )}
        <ActionWrapper title={t('tooltips.edit')}>
          <EditIcon onClick={() => navigate(`/${link}/edit/${item?._id?.toString()}`)} />
        </ActionWrapper>
        {canDelete(item) && (
          <ActionWrapper title={t('tooltips.delete')}>
            <DeleteIcon onClick={() => onClickDelete(item)} />
          </ActionWrapper>
        )}
      </ActionCell>
    )
  }

  const renderActionsPolicyVersion = (item, row, index) => {
    const ActionWrapper = ({ title, onClick, children }) => (
      <ActionIcon title={title} onClick={onClick}>
        {children}
      </ActionIcon>
    )

    // Same draft-only rule as the backend: once sent to review, a version is neither editable nor deletable.
    const canEdit = (policyVersion) => isPolicyVersionEditable(policyVersion)

    const canDelete = (policyVersion) => {
      if (hideDelete) return false
      return isPolicyVersionEditable(policyVersion)
    }

    const canShowAccepts = (policyVersion) => {
      return policyVersion.status === 'Publicado' && !!id
    }

    const canShow = () => {
      return !hideShow && !!id
    }

    return (
      <ActionCell $minWidth={row?.minWidth}>
        {canShowAccepts(item) && (
          <ActionWrapper title={t('tooltips.viewPolicyAccepts')}>
            <PreviewIcon
              onClick={() => navigate(`/${link}/show/${id}/versions/${index}/accepts`)}
            />
          </ActionWrapper>
        )}
        {canShow() && (
          <ActionWrapper title={t('tooltips.viewPolicyVersion')}>
            <ShowIcon
              onClick={() => navigate(`/${link}/show/${item?._id?.toString() ?? id}/versions/${index}`)}
            />
          </ActionWrapper>
        )}
        {canEdit(item) && (
          <ActionWrapper title={t('tooltips.editPolicyVersion')}>
            <EditIcon onClick={() => onClickEdit(index)} />
          </ActionWrapper>
        )}
        {canDelete(item) && (
          <ActionWrapper title={t('tooltips.deletePolicy')}>
            <DeleteIcon onClick={() => onClickDelete(item, index)} />
          </ActionWrapper>
        )}
      </ActionCell>
    )
  }

  const renderActionsRedirect = (item, row) => {
    const ActionWrapper = ({ title, onClick, children }) => (
      <ActionIcon title={title} onClick={onClick}>
        {children}
      </ActionIcon>
    )

    return (
      <ActionCell $minWidth={row?.minWidth}>
        {!hideShow && (
          <ActionWrapper title={t('tooltips.view')}>
            <ShowIcon
              onClick={() => navigate(`/${link}/show/${item?._id?.toString() ?? id}`)}
            />
          </ActionWrapper>
        )}
        <ActionWrapper title={t('tooltips.edit')}>
          <EditIcon
            onClick={() => navigate(`/${link}/edit/${item?._id?.toString() ?? id}`)}
          />
        </ActionWrapper>
        {!hideDelete && (
          <ActionWrapper title={t('tooltips.delete')}>
            <DeleteIcon onClick={() => onClickDelete(item)} />
          </ActionWrapper>
        )}
      </ActionCell>
    )
  }

  const renderActionsStatus = (item, row) => {
    const ActionWrapper = ({ title, onClick, children }) => (
      <ActionIcon title={title} onClick={onClick}>
        {children}
      </ActionIcon>
    )

    return (
      <ActionCell $minWidth={row?.minWidth}>
        {!hideShow && (
          <ActionWrapper title={t('tooltips.view')}>
            <ShowIcon
              onClick={() => {
                if (onClickShow) {
                  onClickShow(item)
                } else {
                  navigate(`/${link}/show/${item?._id?.toString() ?? id}`)
                }
              }}
            />
          </ActionWrapper>
        )}
        {!hideEdit && (
          <ActionWrapper title={t('tooltips.edit')}>
            <EditIcon
              onClick={() => {
                if (onClickEdit) {
                  onClickEdit(item)
                } else {
                  navigate(`/${link}/edit/${item?._id?.toString() ?? id}`)
                }
              }}
            />
          </ActionWrapper>
        )}
        {!hideDelete && item?.active && (
          <ActionWrapper title={t('tooltips.delete')}>
            <DeleteIcon onClick={() => onClickDelete(item)} />
          </ActionWrapper>
        )}
        {!item?.active && (
          <ActionWrapper title={t('tooltips.enable')}>
            <SyncIcon onClick={() => onClickStatus(item)} />
          </ActionWrapper>
        )}
      </ActionCell>
    )
  }

  const renderObjectCell = (item, row) => {
    const fields = row.id.split('.')
    return item[fields[0]]?.[fields[1]]
  }

  const renderArrayObjectCell = (item, row) => {
    const fields = row.id.split('.')
    const content = item[fields[0]]?.findLast(field => field[fields[1]])
    return content ? content[fields[1]] : ''
  }

  const renderArrayObjectDatetimeCell = (item, row) => {
    const fields = row.id.split('.')
    const content = item[fields[0]]?.findLast(field => field[fields[1]] && field[fields[1]] !== null)
    return content?.[fields[1]] ? formatDateTimeToDateTimeBR(content[fields[1]], true) : ''
  }

  const renderConsentCategories = (item, row) => {
    const IconCircle = ({ bgColor, children, title }) => (
      <span
        style={{
          display: 'inline-flex',
          height: '22px',
          width: '22px',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: bgColor,
          borderRadius: '50%',
          marginInlineEnd: '1px',
        }}
        title={title}
      >
        {children}
      </span>
    )

    return (
      <td style={{ textAlign: 'center', alignContent: 'center', border: 0, padding: '12px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <IconCircle
            bgColor={item[row.id]?.required ? `color-mix(in srgb, var(--vo-success) 12%, white)` : 'var(--vo-capri-25)'}
            title={t('cookies.required')}
          >
            <NecessaryIcon width="14px" color={item[row.id]?.required ? 'var(--vo-success)' : 'var(--vo-steel)'} />
          </IconCircle>
          <IconCircle
            bgColor={item[row.id]?.performance ? `color-mix(in srgb, var(--vo-success) 12%, white)` : 'var(--vo-capri-25)'}
            title={t('cookies.performance')}
          >
            <PreferencesIcon width="14px" color={item[row.id]?.performance ? 'var(--vo-success)' : 'var(--vo-steel)'} />
          </IconCircle>
          <IconCircle
            bgColor={item[row.id]?.marketing ? `color-mix(in srgb, var(--vo-success) 12%, white)` : 'var(--vo-capri-25)'}
            title={t('cookies.marketing')}
          >
            <MarketingIcon width="14px" color={item[row.id]?.marketing ? 'var(--vo-success)' : 'var(--vo-steel)'} />
          </IconCircle>
          <IconCircle
            bgColor={item[row.id]?.statistics ? `color-mix(in srgb, var(--vo-success) 12%, white)` : 'var(--vo-capri-25)'}
            title={t('cookies.statistics')}
          >
            <StatisticsIcon width="14px" color={item[row.id]?.statistics ? 'var(--vo-success)' : 'var(--vo-steel)'} />
          </IconCircle>
        </div>
      </td>
    )
  }

  const displayData = (item, row, index) => {
    if (row.render && typeof row.render === 'function') {
      return row.render(item[row.id], item, index)
    }

    if (row.type === 'boolean') {
      return renderBooleanCell(item, row)
    }
    if (row.type === 'actions') {
      return renderActions(item, row, index)
    }
    if (row.type === 'actions-datamapping') {
      return renderActionsDatamapping(item, row, index)
    }
    if (row.type === 'actions-policy') {
      return renderActionsPolicy(item, row)
    }
    if (row.type === 'actions-policy-version') {
      return renderActionsPolicyVersion(item, row, index)
    }
    if (row.type === 'actions-redirect') {
      return renderActionsRedirect(item, row)
    }
    if (row.type === 'actions-status') {
      return renderActionsStatus(item, row)
    }
    if (row.type === 'object') {
      return renderObjectCell(item, row)
    }
    if (row.type === 'date') {
      return formatDateToDateBRPolish(item[row.id])
    }
    if (row.type === 'datebr') {
      return formatDateToDateBR(item[row.id])
    }
    if (row.type === 'datetime') {
      return formatDateTimeToDateTimeBR(item[row.id], true)
    }
    if (row.type === 'time-expires') {
      if (item[row.id] === -1) {
        return t('timeExpires.session')
      } else if (Number((item[row.id] / 1000).toFixed(0)) < 60) {
        return t('timeExpires.seconds', { count: Number((item[row.id] / 1000).toFixed(0)) })
      } else if (Number((item[row.id] / 60000).toFixed(0)) < 60) {
        return t('timeExpires.minutes', { count: Number((item[row.id] / 60000).toFixed(0)) })
      } else if (Number((item[row.id] / 3600000).toFixed(0)) < 24) {
        return t('timeExpires.hours', { count: Number((item[row.id] / 3600000).toFixed(0)) })
      } else {
        const days = Number((item[row.id] / 3600000).toFixed(0)) / 24
        return t('timeExpires.days', { count: Number(days.toFixed(0)) })
      }
    }
    if (row.type === 'category-cookie') {
      switch (item[row.id]) {
        case 'marketing':
          return t('cookies.marketing')
        case 'functional':
          return t('cookies.functional')
        case 'performance':
          return t('cookies.performance')
        case 'statistics':
          return t('cookies.statistics')
        case 'preferences':
          return t('cookies.preferences')
        case 'required':
          return t('cookies.required')
        default:
          return t('cookies.unclassified')
      }
    }
    if (row.type === 'consent-categories') {
      return renderConsentCategories(item, row)
    }
    if (row.type === 'consent-status') {
      return item[row.id] === 'accepted' ? t('cookies.accepted') : t('cookies.rejected')
    }
    if (row.type === 'array-object') {
      return renderArrayObjectCell(item, row)
    }
    if (row.type === 'array-object-datetime') {
      return renderArrayObjectDatetimeCell(item, row)
    }
    if (row.type === 'text' && item[row.id] === 'null') {
      return ''
    }
    return item[row.id]
  }

  const renderSortArrow = (row) => {
    if (!row.sortable) return null
    const isActive = orderBy === row.id
    return (
      <SortArrow $active={isActive}>
        {isActive ? (order === 'asc' ? '\u25B2' : '\u25BC') : '\u25B2\u25BC'}
      </SortArrow>
    )
  }

  return (
    <TableContainer>
      {enableSearch && (
        <SearchHeader>
          <Title>{title}</Title>
          <TextField
            placeholder={t('labels.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 200 }}
          />
        </SearchHeader>
      )}
      <Table $noData={noData()} $applyMB={applyMB}>
        <TableHead>
          <tr>
            {expandable && (
              <TableHeadCell style={{ width: '36px', padding: '0 8px' }} />
            )}
            {rows.map((row) => (
              <TableHeadCell
                key={`thrc-${row.value}`}
                $sortable={row.sortable}
                onClick={() => row.sortable && handleSort(row.id)}
              >
                {row.value}
                {renderSortArrow(row)}
              </TableHeadCell>
            ))}
          </tr>
        </TableHead>
        <TableBody>
          {paginatedCells && paginatedCells.map((item, index) => {
            const rowKey = item?._id?.toString() ?? index
            const isOpen = expandable && openRows.has(rowKey)
            return (
              <React.Fragment key={`tr-${rowKey}`}>
                <TableRow
                  $clickable={useOnClickDefault && !!onClick}
                  onClick={() => {
                    if (useOnClickDefault && onClick) onClick(item)
                  }}
                >
                  {expandable && (
                    <td
                      style={{ width: '36px', padding: '0 8px', verticalAlign: 'middle', border: 0 }}
                      onClick={(e) => { e.stopPropagation(); toggleRow(rowKey) }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          cursor: 'pointer',
                          color: 'var(--vo-slate)',
                          transition: 'transform 0.2s ease',
                          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                          <path d="M7 10l5 5 5-5z" />
                        </svg>
                      </span>
                    </td>
                  )}
                  {rows.map((row) => {
                    const isAction = row.type && row.type.startsWith('actions')
                    const content = displayData(item, row, index)
                    const displayContent = isAction
                      ? content
                      : textEllipsis(content, row?.maxSize || 60)

                    if (isAction) {
                      return <React.Fragment key={row.id}>{content}</React.Fragment>
                    }

                    return (
                      <TableCell
                        key={row.id}
                        $maxWidth={row?.maxSize ? `${row.maxSize}ch` : undefined}
                        title={typeof displayContent === 'string' ? displayContent : ''}
                        style={row?.minWidth ? { minWidth: row.minWidth } : {}}
                      >
                        {displayContent}
                      </TableCell>
                    )
                  })}
                </TableRow>
                {expandable && renderExpanded && (
                  <tr
                    style={{
                      display: isOpen ? 'table-row' : 'none',
                      background: 'var(--vo-white)',
                    }}
                  >
                    <td
                      colSpan={rows.length + 1}
                      style={{ padding: '12px 16px 16px', border: 0, borderBottom: '1px solid var(--vo-cloud)' }}
                    >
                      {renderExpanded(item)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
          {showNoDataLine && noData() && (
            <TableRow key="tr-no-data-line">
              <NoDataCell colSpan={expandable ? rows.length + 1 : rows.length}>
                {t('table.noData')}
              </NoDataCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {pagination && (
        <PaginationWrapper>
          <RowsPerPage>
            <span>{t('table.rowsPerPage')}</span>
            <Select
              name="rowsPerPage"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              options={[5, 10, 25, 50].map((n) => ({ value: n, label: String(n) }))}
              sx={{ '.MuiOutlinedInput-input': { padding: '4px 8px', fontSize: 14 } }}
            />
          </RowsPerPage>
          <PageInfo>
            {t('pagination.page', { current: page + 1, total: Math.ceil(sortedCells.length / rowsPerPage) })}
          </PageInfo>
          <PageButton
            onClick={() => handleChangePage(null, page - 1)}
            disabled={page === 0}
            $disabled={page === 0}
          >
            &lt;
          </PageButton>
          <PageButton
            onClick={() => handleChangePage(null, page + 1)}
            disabled={page >= Math.ceil(sortedCells.length / rowsPerPage) - 1}
            $disabled={page >= Math.ceil(sortedCells.length / rowsPerPage) - 1}
          >
            &gt;
          </PageButton>
        </PaginationWrapper>
      )}
    </TableContainer>
  )
}
